/**
 * TakeoffSessionPanel — floating session summary + expandable review drawer.
 */

import React, { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, Trash2, Edit2, Save, Loader2, MapPin, X, Plus, Download } from 'lucide-react';
import TakeoffExportModal from './TakeoffExportModal';
import type { PdfMeasurement, PdfTakeoffLink, PdfTakeoffStructure } from '../../types';
import {
  formatTimberCurrency,
  parseDimensions,
  pushTakeoffToProposal,
  standardizeTimberDimensions,
  timberItemFromLinkFields,
} from '../../utils/timberTakeoffHelpers';
import { deleteTakeoffLink } from '../../utils/pdfTakeoffLinkService';
import { createTakeoffStructure } from '../../utils/pdfTakeoffStructureService';
import './TakeoffSessionPanel.css';

export interface TakeoffSessionPanelProps {
  links: PdfTakeoffLink[];
  measurements: PdfMeasurement[];
  draftStructures: PdfTakeoffStructure[];
  dealId: string;
  leadId: string;
  documentId: string;
  userId: string;
  userName: string;
  dealTitle?: string;
  contactName?: string;
  projectId?: string | null;
  onEditLink: (link: PdfTakeoffLink, measurement: PdfMeasurement) => void;
  onJumpToMeasurement: (pageNumber: number, measurementId: string) => void;
  onLinksChanged: () => void;
  onRequestPin?: (structureId: string, structureName: string) => void;
  pinModeActive?: boolean;
}

export function TakeoffSessionPanel({
  links,
  measurements,
  draftStructures,
  dealId,
  leadId,
  documentId,
  userId,
  userName,
  dealTitle,
  contactName,
  projectId = null,
  onEditLink,
  onJumpToMeasurement,
  onLinksChanged,
  onRequestPin,
  pinModeActive = false,
}: TakeoffSessionPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [newStructureName, setNewStructureName] = useState('');
  const [creatingStructure, setCreatingStructure] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  const measurementMap = useMemo(() => {
    const map = new Map<string, PdfMeasurement>();
    for (const m of measurements) {
      if (m.id) map.set(m.id, m);
    }
    return map;
  }, [measurements]);

  const linkedMeasurementIds = useMemo(
    () => new Set(links.map(l => l.measurementId)),
    [links],
  );

  const unlinkedCount = measurements.filter(m => m.id && !linkedMeasurementIds.has(m.id)).length;

  const totals = useMemo(() => {
    let boardFeet = 0;
    let cost = 0;
    for (const link of links) {
      const bf = parseDimensions(link.dimensions);
      boardFeet += bf * link.quantity;
      cost += (link.unitCost ?? 0) * link.quantity;
    }
    return { boardFeet, cost, count: links.length };
  }, [links]);

  const handleDelete = async (link: PdfTakeoffLink) => {
    if (!link.id) return;
    if (!window.confirm('Remove this take-off item?')) return;
    try {
      await deleteTakeoffLink(link.id);
      onLinksChanged();
    } catch (err) {
      console.error('Error deleting takeoff link:', err);
    }
  };

  const handleSaveAll = async () => {
    if (links.length === 0) return;
    if (
      !window.confirm(
        'Push take-off items to the proposal? Timber lists will be updated for structures in this session. Other proposal structures are left unchanged.',
      )
    ) {
      return;
    }
    setSaving(true);
    setSaveMessage(null);
    try {
      const items = links.map(link =>
        timberItemFromLinkFields({
          structureId: link.structureId,
          structureName: link.structureName,
          name: link.name,
          species: link.species,
          grade: link.grade,
          dimensions: link.dimensions,
          quantity: link.quantity,
          unitCost: link.unitCost ?? 0,
          moistureContent: link.moistureContent,
          sourceMeasurementId: link.measurementId,
          sourceDocumentId: documentId,
        }),
      );
      const result = await pushTakeoffToProposal(dealId, leadId, items, draftStructures, {
        userId,
        contactName,
        projectName: dealTitle,
      });
      setSaveMessage(result.message);
    } catch (err) {
      console.error('Error pushing takeoff to proposal:', err);
      setSaveMessage('Failed to push to proposal');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateStructure = async () => {
    const trimmed = newStructureName.trim();
    if (!trimmed) return;
    setCreatingStructure(true);
    try {
      await createTakeoffStructure({
        dealId,
        leadId,
        name: trimmed,
        userId,
        userName,
      });
      setNewStructureName('');
    } catch (err) {
      console.error('Error creating structure:', err);
    } finally {
      setCreatingStructure(false);
    }
  };

  const structureIds = useMemo(() => {
    const seen = new Map<string, string>();
    for (const s of draftStructures) {
      if (s.id) seen.set(s.id, s.name);
    }
    for (const link of links) {
      if (!seen.has(link.structureId)) seen.set(link.structureId, link.structureName);
    }
    return Array.from(seen.entries());
  }, [draftStructures, links]);

  const linksByStructure = useMemo(() => {
    const byId = new Map<string, PdfTakeoffLink[]>();
    for (const link of links) {
      const list = byId.get(link.structureId) ?? [];
      list.push(link);
      byId.set(link.structureId, list);
    }

    const orderedIds = structureIds.map(([id]) => id);
    for (const id of Array.from(byId.keys())) {
      if (!orderedIds.includes(id)) orderedIds.push(id);
    }

    return orderedIds
      .filter(id => byId.has(id))
      .map(id => {
        const groupLinks = [...(byId.get(id) ?? [])].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }),
        );
        return {
          structureId: id,
          structureName: structureIds.find(([sid]) => sid === id)?.[1] ?? groupLinks[0]?.structureName ?? 'Structure',
          links: groupLinks,
        };
      });
  }, [links, structureIds]);

  return (
    <div className={`takeoff-session-panel ${expanded ? 'expanded' : 'collapsed'}`}>
      <button
        type="button"
        className="takeoff-session-pill"
        onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
      >
        <span className="takeoff-session-pill-text">
          {totals.count} item{totals.count !== 1 ? 's' : ''}
          {unlinkedCount > 0 && ` · ${unlinkedCount} unlinked`}
          {' · '}{totals.boardFeet.toFixed(0)} BF
          {' · '}{formatTimberCurrency(totals.cost)}
        </span>
        {expanded ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
      </button>

      {expanded && (
        <div className="takeoff-session-drawer">
          <div className="takeoff-session-drawer-header">
            <h3>Take-off Session</h3>
            <button type="button" className="takeoff-session-close" onClick={() => setExpanded(false)} aria-label="Collapse">
              <X size={16} />
            </button>
          </div>

          <div className="takeoff-session-structures">
            <span className="takeoff-session-structures-label">Structures</span>
            <div className="takeoff-session-structure-chips">
              {draftStructures.length === 0 && structureIds.length === 0 && (
                <span className="takeoff-session-structures-empty">None yet — add one below</span>
              )}
              {draftStructures.map(s => (
                <span key={s.id} className="takeoff-session-structure-chip">{s.name}</span>
              ))}
            </div>
            <div className="takeoff-session-add-structure">
              <input
                type="text"
                value={newStructureName}
                onChange={e => setNewStructureName(e.target.value)}
                placeholder="New structure name"
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleCreateStructure();
                  }
                }}
              />
              <button
                type="button"
                disabled={creatingStructure || !newStructureName.trim()}
                onClick={() => void handleCreateStructure()}
              >
                {creatingStructure ? <Loader2 size={14} className="spin" /> : <Plus size={14} />}
              </button>
            </div>
          </div>

          {structureIds.length > 0 && onRequestPin && (
            <div className="takeoff-session-pin-row">
              {structureIds.map(([sid, sname]) => (
                <button
                  key={sid}
                  type="button"
                  className="takeoff-session-pin-btn"
                  disabled={pinModeActive}
                  onClick={() => onRequestPin(sid, sname)}
                  title={`Add a label for ${sname} on this page (use again for another view)`}
                >
                  <MapPin size={12} /> {sname}
                </button>
              ))}
            </div>
          )}

          {links.length === 0 ? (
            <p className="takeoff-session-empty">Draw a measurement to add timber items.</p>
          ) : (
            <div className="takeoff-session-table-wrap">
              <table className="takeoff-session-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Species</th>
                    <th>Dimensions</th>
                    <th>Qty</th>
                    <th>Bd Ft</th>
                    <th>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {linksByStructure.map(group => {
                    let groupBoardFeet = 0;
                    let groupCost = 0;
                    for (const link of group.links) {
                      groupBoardFeet += parseDimensions(link.dimensions) * link.quantity;
                      groupCost += (link.unitCost ?? 0) * link.quantity;
                    }
                    return (
                      <React.Fragment key={group.structureId}>
                        <tr className="takeoff-session-structure-row">
                          <td colSpan={4}>
                            <span className="takeoff-session-structure-row-name">{group.structureName}</span>
                            <span className="takeoff-session-structure-row-count">
                              {group.links.length} item{group.links.length !== 1 ? 's' : ''}
                            </span>
                          </td>
                          <td>{groupBoardFeet.toFixed(1)}</td>
                          <td>{formatTimberCurrency(groupCost)}</td>
                          <td />
                        </tr>
                        {group.links.map(link => {
                          const bf = parseDimensions(link.dimensions);
                          const total = (link.unitCost ?? 0) * link.quantity;
                          const measurement = measurementMap.get(link.measurementId);
                          return (
                            <tr key={link.id} className="takeoff-session-item-row">
                              <td>
                                <button
                                  type="button"
                                  className="takeoff-session-link-btn"
                                  onClick={() => onJumpToMeasurement(link.pageNumber, link.measurementId)}
                                  title="Jump to measurement"
                                >
                                  {link.name || '—'}
                                </button>
                              </td>
                              <td>{link.species || '—'}</td>
                              <td>{standardizeTimberDimensions(link.dimensions)}</td>
                              <td>{link.quantity}</td>
                              <td>{(bf * link.quantity).toFixed(1)}</td>
                              <td>{formatTimberCurrency(total)}</td>
                              <td className="takeoff-session-actions">
                                {measurement && (
                                  <button
                                    type="button"
                                    className="takeoff-session-icon-btn"
                                    onClick={() => onEditLink(link, measurement)}
                                    aria-label="Edit"
                                  >
                                    <Edit2 size={14} />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="takeoff-session-icon-btn"
                                  onClick={() => void handleDelete(link)}
                                  aria-label="Delete"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="takeoff-session-footer">
            <div className="takeoff-session-footer-actions">
              <button
                type="button"
                className="takeoff-session-export-btn"
                onClick={() => setShowExportModal(true)}
                title="Export internal take-off markup PDF for all plan files on this deal"
              >
                <Download size={16} />
                Internal PDF
              </button>
              <button
                type="button"
                className="takeoff-session-save-btn"
                disabled={saving || links.length === 0}
                onClick={() => void handleSaveAll()}
              >
                {saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />}
                {saving ? 'Saving…' : 'Push to proposal'}
              </button>
            </div>
            {saveMessage && (
              <p className={`takeoff-session-message ${saveMessage.toLowerCase().includes('fail') || saveMessage.includes('not found') ? 'error' : 'success'}`}>
                {saveMessage}
              </p>
            )}
          </div>
        </div>
      )}

      <TakeoffExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        dealId={dealId}
        dealTitle={dealTitle}
        contactName={contactName}
        userName={userName}
        projectId={projectId}
        hasLinks={links.length > 0}
      />
    </div>
  );
}

export default TakeoffSessionPanel;
